import { DIALOG_DATA } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  CancelOrderDialogData,
  EReturnOrderReason,
  ECancelOrderReason,
  ReasonKey,
  ReturnOrderDialogData,
  returnReasonMap,
  ApiResponse,
  ApiErrorResponse,
  isErrorResponse,
} from '@shabic/models';
import { DialogComponent } from '../dialog/dialog.component';
import { OrderMediaService } from '@shabic/core';
import { switchMap, catchError, of } from 'rxjs';

@Component({
  selector: 'shabic-return-order-dialog',
  templateUrl: './return-order-dialog.component.html',
  styleUrls: ['./return-order-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReturnOrderDialogComponent {
  readonly EReturnOrderReason = EReturnOrderReason;
  readonly ECancelOrderReason = ECancelOrderReason;
  readonly formGroup = new FormGroup({
    reason: new FormControl(null),
    description: new FormControl(''),
  });

  selectedFiles: File[] = [];
  filePreviews: Map<File, string> = new Map();
  isUploading = false;

  get reasonControl() {
    return this.formGroup.get('reason') as FormControl;
  }

  get isReturnReasons() {
    return this.data instanceof ReturnOrderDialogData;
  }

  get isCancelReasons() {
    return this.data instanceof CancelOrderDialogData;
  }

  constructor(
    @Inject(DIALOG_DATA)
    public data: ReturnOrderDialogData | CancelOrderDialogData,
    private translateService: TranslateService,
    private orderMediaService: OrderMediaService
  ) {}



  getReasonKey(reason: ReasonKey) {
    return returnReasonMap.get(reason) || '';
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
          this.selectedFiles.push(file);
          this.createFilePreview(file);
        }
      }
    }
  }

  createFilePreview(file: File) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.filePreviews.set(file, e.target.result);
    };
    reader.readAsDataURL(file);
  }

  getFilePreview(file: File): string {
    return this.filePreviews.get(file) || '';
  }

  removeFile(index: number) {
    const file = this.selectedFiles[index];
    this.selectedFiles.splice(index, 1);
    this.filePreviews.delete(file);
  }

  confirm(dialog: DialogComponent) {
    const reason = this.formGroup.value.reason as unknown as ReasonKey;
    const description =
      returnReasonMap.get(reason) || this.formGroup.value.description || '';

    // Step 1: Upload images if any are selected
    if (this.selectedFiles.length > 0) {
      this.isUploading = true;
      console.log('Starting image upload for', this.selectedFiles.length, 'files');
      this.orderMediaService
        .uploadOrderImages(this.selectedFiles)
        .pipe(
          switchMap((uploadResponse: ApiResponse<number[] | ApiErrorResponse>) => {
            console.log('Upload response received:', uploadResponse);
            if (isErrorResponse(uploadResponse)) {
              console.error('Image upload failed:', uploadResponse);
              this.isUploading = false;
              return of(null);
            }
            
            // Media IDs are already extracted by the service
            const mediaIds = uploadResponse.payload;
            console.log('Media IDs from service:', mediaIds);
            
            // Ensure we have valid media IDs (not an error response)
            if (Array.isArray(mediaIds)) {
              // Step 2: Call the return/cancel endpoint with media IDs
              return this.data
                .onConfirm({
                  description: this.translateService.instant(description),
                  photos: mediaIds, // Pass media IDs instead of files
                });
            } else {
              console.error('Invalid media IDs received:', mediaIds);
              this.isUploading = false;
              return of(null);
            }
          }),
          catchError(error => {
            console.error('Error in return order process:', error);
            this.isUploading = false;
            return of(null);
          })
        )
        .subscribe((result) => {
          console.log('Final result:', result);
          this.isUploading = false;
          if (result !== null) {
            dialog.close();
          }
        });
    } else {
      console.log('No files to upload, proceeding directly with return/cancel');
      // No images to upload, proceed directly with return/cancel
      this.data
        .onConfirm({
          description: this.translateService.instant(description),
          photos: [], // No photos
        })
        .subscribe(() => {
          dialog.close();
        });
    }
  }
}
